import React, { useEffect, useCallback, useRef, useState } from 'react';
import {
  Keyboard,
  Platform,
  useWindowDimensions,
  TouchableOpacity,
  Animated,
  StatusBar,
  ImageSourcePropType,
  Dimensions
} from 'react-native';

import styled from 'styled-components/native';
import Colors from '../constants/Colors';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useUserContext } from '../context/UserContext';
import { Ionicons, Octicons } from '@expo/vector-icons';
import {
  useNavigationState,
  DrawerActions,
  useRoute,
  RouteProp,
  useFocusEffect
} from '@react-navigation/native';
import { DrawerParamList } from '../navigation/DrawerNavigator';
import { BackHandler } from 'react-native';
import IconBadge from './IconBadge';
import { usePubSubNewUserMessage } from '../hooks/session/usePubSubNewUserMessage';
import { useGetRoomList } from '../hooks/room/useGetRoomList';
import { H4, P } from './Typography';
import { useFormNavigation } from '../dynamic-forms/navigation/FormNavigationContext';
import { StudyHeaderConfig, useAppContext } from '../AppContext';
import ChloeLogo from './ChloeLogo';
import AppManifest from '../common/AppManifest';
import elementIDs from '../../elementIDs';

const StyledContainerView = styled.View<{
  isStudyHeader?: boolean;
}>`
  width: 100%;
  /* height: ${p =>
    p.isStudyHeader ? 'auto' : Platform.OS === 'web' ? '68px' : 'auto'}; */
  background-color: ${p =>
    p.isStudyHeader ? Colors.primary : Colors.background};
`;

const StyledTopBarView = styled.View<{
  isStudyHeader?: boolean;
  isLandscape?: boolean;
}>`
  ${Platform.OS !== 'web' ? 'margin-top: 16px;' : ''}
  flex-direction: row;
  align-items: center;
  background-color: transparent;
  padding: ${p =>
    Platform.OS === 'ios'
      ? `${p.isLandscape ? 16 : 48}px 16px 16px 16px`
      : Platform.OS === 'android'
        ? `${p.isLandscape ? 16 : 34}px 16px 16px 16px`
        : '16px 18px'};
  /* position: ${Platform.OS === 'web' ? 'fixed' : 'relative'}; */
  width: 100%;
`;
const StyledTodayContentView = styled(Animated.View)`
  /* margin-bottom: -24px; */
  overflow: hidden;
`;

const StyledBackgroundImage = styled.Image`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 0;
  /* background-color: white; */
`;

const StyledLogoContainer = styled.View`
  padding: 0 24px;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledBackButtonView = styled.TouchableOpacity<{
  isLandscape: boolean;
}>`
  padding: ${p =>
    Platform.OS === 'ios'
      ? `${p.isLandscape ? 16 : 48}px 0 16px 16px`
      : Platform.OS === 'android'
        ? `${p.isLandscape ? 16 : 34}px 0 16px 16px`
        : '16px 0 16px 16px'};
  top: 0;
  left: 0;
  bottom: 0;
  position: absolute;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  z-index: 1;
`;

const StyledToolbarView = styled.View<{
  isLandscape: boolean;
}>`
  padding: ${p =>
    Platform.OS === 'ios'
      ? `${p.isLandscape ? 16 : 48}px 16px 16px 16px`
      : Platform.OS === 'android'
        ? `${p.isLandscape ? 16 : 34}px 16px 16px 16px`
        : '16px 18px'};
  top: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const StyledIconMenuContainer = styled.TouchableOpacity`
  position: relative;
  margin-left: 18px;
`;

const StyledHeaderNavContainer = styled.View`
  margin-left: 80px;
  flex-direction: row;
  width: 400px;
  justify-content: space-between;
  height: 45px;
  margin: 10px 0 -16px 50px;
`;

const StyledHeaderNavButton = styled.TouchableOpacity<{
  focused: boolean;
  isStudyHeader?: boolean;
  borderColor?: string;
}>`
  border-bottom-color: ${p =>
    p.focused
      ? p.isStudyHeader
        ? p.borderColor || Colors.switchActiveThumbColor
        : Colors.switchActiveThumbColor
      : 'transparent'};
  border-bottom-width: 4px;
  padding: 0 10px;
`;

const StyledHeaderNavText = styled.Text<{
  focused: boolean;
  isStudyHeader?: boolean;
  textColor?: string;
}>`
  font-family: ${p => (p.focused ? 'WorkSans-SemiBold' : 'WorkSans-Regular')};
  font-size: 16px;
  line-height: 26.48px;
  letter-spacing: -0.11px;
  color: ${p =>
    p.focused
      ? p.isStudyHeader
        ? p.textColor || Colors.switchActiveThumbColor
        : Colors.switchActiveThumbColor
      : p.isStudyHeader
        ? p.textColor || 'white'
        : Colors.primary};
`;

const StyledHeaderTitleButton = styled.TouchableOpacity`
  background-color: transparent;
  width: 100%;
  align-items: center;
  /* leaves room for the caret alongside the longest line */
  padding: 0 32px;
`;

const StyledHeaderTitleContent = styled.View`
  position: relative;
`;

const StyledHeaderTitleCaret = styled.View`
  position: absolute;
  right: -26px;
  top: 0;
  bottom: 0;
  justify-content: center;
`;

const StyledRewardPointsContainer = styled.View<{ hasBackButton: boolean }>`
  padding: ${Platform.OS === 'ios'
    ? '48px 16px 16px 16px'
    : Platform.OS === 'android'
      ? '34px 16px 16px 16px'
      : '16px 18px'};
  top: 0;
  left: ${p => (p.hasBackButton ? 25 : 0)}px;
  bottom: 0;
  position: absolute;
`;

const handleBack = (
  fromStudyGenerator: boolean,
  navigation: StackNavigationProp<any>
) => {
  if (fromStudyGenerator) {
    navigation.reset({
      index: 0,
      routes: [
        {
          name: 'StudiesNav',
          state: {
            index: 1,
            routes: [
              { name: 'MyStudies' },
              {
                name: 'FormTemplate',
                params: {
                  formTemplateName: 'Start Study'
                }
              }
            ]
          }
        }
      ]
    });
  } else if (navigation.canGoBack()) {
    navigation.goBack();
  } else {
    BackHandler.exitApp();
  }
};

const StudyHeaderTitle: React.FC<{
  config: StudyHeaderConfig;
  captionOpacity: Animated.AnimatedInterpolation<number>;
}> = ({ config, captionOpacity }) => {
  const foreground = config?.foregroundColor ?? 'light';
  const content = (
    <>
      <H4
        center
        color={foreground}
        padding={1}
        testID={elementIDs.todayStudyNameID}
      >
        {config?.title || 'Study'}
      </H4>
      {!!config?.caption && (
        // React Native Animated View Interpolation Fade out
        <Animated.View style={{ opacity: captionOpacity }}>
          <P center color={foreground}>
            {config.caption}
          </P>
        </Animated.View>
      )}
    </>
  );
  if (!config?.onTitlePress) {
    return content;
  }
  return (
    <StyledHeaderTitleButton
      accessibilityRole='button'
      activeOpacity={0.7}
      onPress={config.onTitlePress}
    >
      <StyledHeaderTitleContent>
        {content}
        <StyledHeaderTitleCaret>
          <Ionicons
            name='chevron-down'
            size={20}
            color={config.foregroundColor || 'white'}
          />
        </StyledHeaderTitleCaret>
      </StyledHeaderTitleContent>
    </StyledHeaderTitleButton>
  );
};

export type AppHeaderProps = {
  disableNavigation?: boolean;
  onQuestionPressed?: () => void;
  onClose?: () => void;
};

const AppHeader: React.FC<AppHeaderProps> = ({
  disableNavigation,
  onQuestionPressed,
  onClose
}) => {
  const window = useWindowDimensions();
  const isDesktop = window.width >= 940;
  const isMobile = window.width < 400;
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { headerConfig, scrollY } = useAppContext();
  const { user, updateUserSession, pastOccurrences, accessToken } =
    useUserContext();
  const navigationState = useNavigationState(state => state);
  const { requestBack } = useFormNavigation();
  const backInFlightRef = useRef(false);
  const goBackOnce = useCallback(
    async (fromStudyGenerator: boolean) => {
      if (backInFlightRef.current) return;
      backInFlightRef.current = true;
      try {
        if (await requestBack()) return;
        handleBack(fromStudyGenerator, navigation);
      } finally {
        backInFlightRef.current = false;
      }
    },
    [requestBack, navigation]
  );
  const route = useRoute<RouteProp<DrawerParamList, 'FormTemplate'>>();

  const parentState = navigation.getParent()?.getState();
  const state = navigation.getState();
  const tabRoute = parentState?.routes[parentState.index].name || '';
  const focusedRoute = state?.routes[state.index].name || '';

  const [isLandscape, setIsLandscape] = useState<boolean>(false);

  useEffect(() => {
    const orientationHandler = Dimensions.addEventListener(
      'change',
      ({ window: { width, height } }) => {
        if (width < height) {
          setIsLandscape(false);
        } else {
          setIsLandscape(true);
        }
      }
    );
    return () => orientationHandler.remove();
  }, []);

  useEffect(() => {
    let removeBackHandler: (() => void) | undefined;
    const setFocused = () => {
      removeBackHandler?.();
      removeBackHandler = undefined;
      if (Platform.OS === 'android') {
        const back = () => {
          goBackOnce(
            route.name === 'FormTemplate' &&
              route.params?.formTemplateName === undefined &&
              route.params?.path === undefined &&
              navigationState.routes.some(
                (route: any) => route.params?.formTemplateName === 'Start Study'
              )
          );
          return true;
        };
        const backHandler = BackHandler.addEventListener(
          'hardwareBackPress',
          back
        );
        removeBackHandler = () => backHandler.remove();
      }
    };
    navigation.addListener('focus', setFocused);
    return () => {
      removeBackHandler?.();
      navigation.removeListener('focus', setFocused);
    };
  }, [
    navigation,
    navigationState.routes,
    goBackOnce,
    route.name,
    route.params?.formTemplateName,
    route.params?.path
  ]);

  const { roomList, loadingRooms, getRooms } = useGetRoomList();
  // refresh the room list on focus
  useFocusEffect(
    useCallback(() => {
      getRooms();
    }, [getRooms])
  );

  // refresh user.unreadMessageCount when receiving new messages
  const { refreshToken } = usePubSubNewUserMessage(
    user?.id || 0,
    'AppHeader',
    true
  );
  useEffect(() => {
    if (refreshToken) {
      updateUserSession();
      getRooms();
    }
  }, [getRooms, refreshToken, updateUserSession]);

  const isStudyHeader =
    !!headerConfig && ['ToDo', 'StudyInfo'].includes(focusedRoute);

  useFocusEffect(
    useCallback(() => {
      // this effect will run whenever isStudyHeader changes.
      const statusBarStyle = isStudyHeader ? 'light-content' : 'dark-content';
      StatusBar.setBarStyle(statusBarStyle);

      return () => {
        // change the status bar color back to dark after leaving from these routes
        if (tabRoute === 'ToDoNav' || tabRoute === 'StudiesNav') {
          StatusBar.setBarStyle('dark-content');
        }
      };
    }, [isStudyHeader])
  );

  // handle Today Tab header scroll animation interpolation
  const scale = scrollY.interpolate({
    inputRange: [0, 138],
    outputRange: [1, 0.8],
    extrapolateRight: 'clamp'
  });
  const opacity = scrollY.interpolate({
    inputRange: [0, 138],
    outputRange: [1, 0],
    extrapolate: 'clamp'
  });
  // Freeze layout margins on web (animating them reflows page height → scrollbar shake on Windows); animate on native.
  const marginTop =
    Platform.OS === 'web'
      ? 16
      : scrollY.interpolate({
          inputRange: [0, 138],
          outputRange: [16, 0],
          extrapolate: 'clamp'
        });
  const marginBottom =
    Platform.OS === 'web'
      ? 24
      : scrollY.interpolate({
          inputRange: [0, 138],
          outputRange: [24, headerConfig?.caption ? -8 : 12],
          extrapolate: 'clamp'
        });
  return (
    <StyledContainerView isStudyHeader={isStudyHeader}>
      {isStudyHeader && headerConfig.backgroundSource && (
        <StyledBackgroundImage
          resizeMode='cover'
          source={
            typeof headerConfig.backgroundSource === 'string'
              ? headerConfig.backgroundSource.includes('http')
                ? {
                    uri: headerConfig.backgroundSource + `&token=${accessToken}`
                  }
                : (headerConfig.backgroundSource as ImageSourcePropType)
              : headerConfig.backgroundSource
          }
        />
      )}
      <StyledTopBarView isStudyHeader={isStudyHeader} isLandscape={isLandscape}>
        {focusedRoute !== 'MyDataNav' && user && navigationState.index > 0 && (
          <StyledBackButtonView
            isLandscape={isLandscape}
            activeOpacity={0.7}
            onPress={() => {
              goBackOnce(
                route.name === 'FormTemplate' &&
                  route.params?.formTemplateName === undefined &&
                  route.params?.path === undefined &&
                  navigationState.routes.some(
                    (route: any) =>
                      route.params?.formTemplateName === 'Start Study'
                  )
              );
            }}
          >
            <Octicons
              testID={elementIDs.headerBackIconID}
              name='chevron-left'
              size={30}
              color={
                isStudyHeader
                  ? headerConfig.foregroundColor || 'white'
                  : 'black'
              }
            />
            {/* <Feather
              name='arrow-left'
              size={24}
              color={isStudyHeader ? 'white' : 'black'}
            /> */}
          </StyledBackButtonView>
        )}

        <StyledLogoContainer>
          <TouchableOpacity
            disabled={Platform.OS !== 'web'}
            activeOpacity={0.7}
            onPress={() => {
              if (!disableNavigation && user) {
                navigation.navigate('BottomTabNav', {
                  screen: 'HomeNav',
                  params: {
                    screen: 'MyChloe'
                  }
                });
              }
            }}
            style={{ zIndex: 1 }}
          >
            <ChloeLogo
              color={
                isStudyHeader ? headerConfig.foregroundColor || 'light' : 'dark'
              }
            />
          </TouchableOpacity>
          {!disableNavigation && user && Platform.OS === 'web' && isDesktop && (
            <StyledHeaderNavContainer>
              <StyledHeaderNavButton
                focused={tabRoute === 'ToDoNav'}
                isStudyHeader={isStudyHeader}
                borderColor={headerConfig?.foregroundColor}
                onPress={() => navigation.navigate('ToDoNav')}
              >
                <StyledHeaderNavText
                  isStudyHeader={isStudyHeader}
                  textColor={headerConfig?.foregroundColor}
                  focused={tabRoute === 'ToDoNav'}
                >
                  Today
                </StyledHeaderNavText>
                {pastOccurrences.length > 0 && (
                  <IconBadge
                    value={pastOccurrences.length}
                    top={-7.5}
                    right={-7.5}
                  />
                )}
              </StyledHeaderNavButton>

              {/* <StyledHeaderNavButton
                onPress={() => navigation.navigate('HomeNav')}
                focused={tabRoute === 'HomeNav'}
              >
                <StyledHeaderNavText isStudyHeader={isStudyHeader} focused={tabRoute === 'HomeNav'}>
                  Home
                </StyledHeaderNavText>
              </StyledHeaderNavButton> */}
              <StyledHeaderNavButton
                onPress={() => navigation.navigate('DiscoverNav')}
                borderColor={headerConfig?.foregroundColor}
                isStudyHeader={isStudyHeader}
                focused={tabRoute === 'DiscoverNav'}
              >
                <StyledHeaderNavText
                  isStudyHeader={isStudyHeader}
                  textColor={headerConfig?.foregroundColor}
                  focused={tabRoute === 'DiscoverNav'}
                >
                  Join
                </StyledHeaderNavText>
              </StyledHeaderNavButton>
              <StyledHeaderNavButton
                focused={tabRoute === 'StudiesNav'}
                borderColor={headerConfig?.foregroundColor}
                isStudyHeader={isStudyHeader}
                onPress={() => navigation.navigate('StudiesNav')}
              >
                <StyledHeaderNavText
                  isStudyHeader={isStudyHeader}
                  textColor={headerConfig?.foregroundColor}
                  focused={tabRoute === 'StudiesNav'}
                >
                  My Studies
                </StyledHeaderNavText>
              </StyledHeaderNavButton>
              <StyledHeaderNavButton
                onPress={() => navigation.navigate('MyDataNav')}
                borderColor={headerConfig?.foregroundColor}
                isStudyHeader={isStudyHeader}
                focused={focusedRoute === 'MyDataNav'}
              >
                <StyledHeaderNavText
                  isStudyHeader={isStudyHeader}
                  textColor={headerConfig?.foregroundColor}
                  focused={focusedRoute === 'MyDataNav'}
                >
                  My Data
                </StyledHeaderNavText>
              </StyledHeaderNavButton>
            </StyledHeaderNavContainer>
          )}
        </StyledLogoContainer>
        {/* {!disableNavigation && user && (
          <StyledRewardPointsContainer
            hasBackButton={navigationState.index > 0}
          >
            <RewardPointsChip />
          </StyledRewardPointsContainer>
        )} */}
        <StyledToolbarView isLandscape={isLandscape}>
          {!disableNavigation && user && (
            <>
              {(Platform.OS !== 'web' || isDesktop) && (
                <>
                  {(['development', 'development-remote'].includes(
                    AppManifest.env
                  ) ||
                    user.enableExperimentZone) && (
                    <StyledIconMenuContainer
                      activeOpacity={0.7}
                      onPress={() => {
                        navigation.navigate('ExamplesNav');
                      }}
                    >
                      <Ionicons
                        name='flask'
                        size={24}
                        color={
                          isStudyHeader
                            ? headerConfig.foregroundColor || 'white'
                            : 'black'
                        }
                      />
                    </StyledIconMenuContainer>
                  )}
                  <StyledIconMenuContainer
                    activeOpacity={0.7}
                    onPress={() => {
                      if (tabRoute === 'MessengerNav') return;
                      navigation.navigate('MessengerNav');
                    }}
                  >
                    <Ionicons
                      testID={elementIDs.messageButtonID}
                      name='chatbubble-ellipses-outline'
                      size={24}
                      color={
                        isStudyHeader
                          ? headerConfig.foregroundColor || 'white'
                          : 'black'
                      }
                    />
                    {user?.unreadMessageCount > 0 && (
                      <IconBadge
                        value={user.unreadMessageCount}
                        top={-8}
                        right={-8}
                      />
                    )}
                  </StyledIconMenuContainer>
                  <StyledIconMenuContainer
                    testID={elementIDs.profileButtonID}
                    activeOpacity={0.7}
                    onPress={() => {
                      navigation.navigate('MyProfileSettingsNav');
                    }}
                  >
                    <Ionicons
                      name='person-circle-outline'
                      size={24}
                      color={
                        isStudyHeader
                          ? headerConfig.foregroundColor || 'white'
                          : 'black'
                      }
                    />
                  </StyledIconMenuContainer>
                </>
              )}
              {Platform.OS === 'web' && !isDesktop && (
                <StyledIconMenuContainer
                  onPress={() => {
                    Keyboard.dismiss();
                    navigation.dispatch(DrawerActions.openDrawer());
                  }}
                >
                  <Ionicons name='menu-sharp' size={28} color='#3A2E9E' />
                </StyledIconMenuContainer>
              )}
            </>
          )}
          {onQuestionPressed && (
            <StyledIconMenuContainer
              activeOpacity={0.7}
              onPress={() => {
                onQuestionPressed?.();
              }}
            >
              <Octicons
                name='question'
                size={24}
                color={isStudyHeader ? 'white' : 'black'}
              />
            </StyledIconMenuContainer>
          )}
          {onClose && (
            <StyledIconMenuContainer
              activeOpacity={0.7}
              onPress={() => onClose?.()}
            >
              <Ionicons
                name='close'
                size={24}
                color={isStudyHeader ? 'white' : 'black'}
              />
            </StyledIconMenuContainer>
          )}
        </StyledToolbarView>
      </StyledTopBarView>
      {isStudyHeader && (
        <StyledTodayContentView
          // React Native Animated View Interpolation
          style={{ marginTop, marginBottom, transform: [{ scale }] }}
        >
          <StudyHeaderTitle config={headerConfig} captionOpacity={opacity} />
        </StyledTodayContentView>
      )}
      {/* {!isStudyHeaderTab && (
        <LinearGradient
          style={{
            // position: 'absolute',
            // bottom: -16,
            width: '100%',
            height: 16,
            zIndex: 2
          }}
          colors={['rgba(253, 251, 247, 1)', 'rgba(253, 251, 247, 0.1)']}
          pointerEvents={'none'}
        />
      )} */}
    </StyledContainerView>
  );
};

export default AppHeader;
